import {
  createContext,
  createEffect,
  JSXElement,
  mergeProps,
  Show,
  untrack,
  useContext
} from 'solid-js';
import { createStore } from 'solid-js/store';
import { useQuery } from '@tanstack/solid-query';
import {
  getEnvAppSessionDetails,
  getEnvAppSessionQueryKey,
  retryAuthorizedRequests
} from '~/api';
import logger from '~/utils/logger';
import SkLoader from '~/components/ui/SkLoader';
import { UserData } from '~/api/types/userData.ts';
import { AppErrors } from '~/components/ErrorBoundaries/utils.ts';
import { HttpStatusCode } from 'axios';

export type EnvAppDataContextValueType = {
  user?: UserData;
  organizationId?: string;
  currentSessionId?: string;
};

const EnvAppDataContext = createContext<EnvAppDataContextValueType>({});

type EnvAppDataContextProviderProps = {
  children: JSXElement;
};

export default function EnvAppDataContextProvider(
  props: EnvAppDataContextProviderProps
): JSXElement {
  const merged = mergeProps({}, props);

  const [contextValue, setContextValue] =
    createStore<EnvAppDataContextValueType>({});

  const sessionDetailsQuery = useQuery(() => ({
    queryKey: getEnvAppSessionQueryKey(),
    queryFn: async () => await getEnvAppSessionDetails(),
    retry: retryAuthorizedRequests
  }));

  const [isDataLoaded, setIsDataLoaded] = createStore<{
    sessionsData: boolean;
  }>({ sessionsData: false });

  createEffect(() => {
    if (sessionDetailsQuery.isSuccess && sessionDetailsQuery.data) {
      untrack(() => {
        if (
          !sessionDetailsQuery.data.user ||
          !sessionDetailsQuery.data.user.memberships.length
        ) {
          throw new Error(AppErrors.ENV_APP_MISSING_SESSION_DETAILS);
        }
        const user = sessionDetailsQuery.data.user;
        setContextValue('user', user);
        setContextValue('organizationId', user.memberships[0].organizationId);
        setContextValue(
          'currentSessionId',
          sessionDetailsQuery.data.currentSessionId
        );
        setIsDataLoaded({
          sessionsData: true
        });
      });
    } else if (sessionDetailsQuery.isError && sessionDetailsQuery.error) {
      logger.error(
        `Failed to fetch session details `,
        sessionDetailsQuery.error
      );
      if (sessionDetailsQuery.error.httpStatus === HttpStatusCode.NotFound) {
        throw new Error(AppErrors.ENV_APP_USER_NOT_FOUND);
      }
    }
  });

  const isAllDataLoaded = () => {
    return isDataLoaded.sessionsData;
  };

  return (
    <Show
      when={isAllDataLoaded()}
      fallback={<SkLoader />}
    >
      <EnvAppDataContext.Provider value={contextValue}>
        {merged.children}
      </EnvAppDataContext.Provider>
    </Show>
  );
}

export const useEnvAppDataContext = () =>
  useContext(EnvAppDataContext) as EnvAppDataContextValueType;
